import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircle, faSearch, faInfo, faXmark, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import Modal from '../../../components/Modal';
import deleteMenu from '../../../assets/images/deleteMenu.png';
import {
  useDeleteQuestionSetsMutation,
  useExportQuestionSetsToXmlMutation,
  useTransferQuestionSetsToAdminMutation,
  useTransferQuestionSetsToTeacherMutation
} from '../../../features/authentication/qSetApiSlice';
import CustomToast from '../../../components/Toast';
import { useSearchMembersMutation } from '../../../features/authentication/assignApiSlice';
import { useGetAdminListMutation } from '../../../features/authentication/studentApiSlice';
import useImportXml from '../utils/useImportXml';
import ImportXmlModal from './ImportXmlModal';
import SetTile from '../components/SetTile';
import { useMyContentContext } from '../utils/myContentContext';
import FloatingBar from './FloatingBar';
import PageNavigation from '../../../components/pageNavigation';
import { getSetType } from '../utils/getSetType';
import UserTypes from '../../../enums/userTypes';
import { useSelector } from 'react-redux';
import { selectCurrentUserData } from '../../../features/authentication/authSlice';

const SetListing = ({
  setSetsList,
  setsList,
  setOpenCreateSet,
  setSearchText,
  searchText,
  pageCount,
  setCurrentPage,
  currentPage,
  getSets,
  setSkip,
  takeSize,
  showDeletedSets,
  handleShowDeletedChanges,
  showDisplayDeletedSetCheckbox
}) => {
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [selectedSetName, setSelectedSetName] = useState('');
  const [openDeleteSetModal, setOpenDeleteSetModal] = useState(false);
  const [openTransferSetToAdminModal, setOpenTransferSetToAdminModal] = useState(false);
  const [openTransferSetToTeacherModal, setOpenTransferSetToTeacherModal] = useState(false);
  const [dateBasedUniqueKey, setDateBasedUniqueKey] = useState(new Date().getTime().toString());
  const [teacherSearchText, setTeacherSearchText] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [adminSearchText, setAdminSearchText] = useState('');
  const [admins, setAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [showRestore, setShowRestore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [teacherSearchInfo, setTeacherSearchInfo] = useState('');
  const searchDebounceRef = useRef(null);
  const user = useSelector(selectCurrentUserData);

  const {
    openImportXmlModal,
    importFile,
    isImportWaiting,
    importLoadingProgress,
    handleImportQuestionSet,
    handleImportFileChange,
    handleUploadImportXml,
    closeModal
  } = useImportXml(() => getSets(0, takeSize, searchText, showDeletedSets));

  const [deleteQuestionSets] = useDeleteQuestionSetsMutation();
  const [exportQuestionSetsToXml] = useExportQuestionSetsToXmlMutation();
  const [searchTeachers] = useSearchMembersMutation();
  const [transferQuestionSetsToTeacher] = useTransferQuestionSetsToTeacherMutation();
  const [getAdminList] = useGetAdminListMutation();
  const [transferQuestionSetsToAdmin] = useTransferQuestionSetsToAdminMutation();

  const { activeTab, selectedSets, setSelectedSets } = useMyContentContext();

  const handleSearch = (searchText) => {
    if (searchText.trim().length > 0) {
      setSearchText(searchText);
      setCurrentPage(1);
      if (searchDebounceRef.current)
        clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        getSets(0, takeSize, searchText, showDeletedSets);
      }, 300);
    }
    else
      clearSearch();
  };

  const handleSelectPage = (pageNumber) => {
    if (pageNumber == currentPage) return;
    const skipValue = (pageNumber - 1) * takeSize;
    getSets(skipValue, takeSize, searchText, showDeletedSets);
    setSkip(skipValue);
    setCurrentPage(pageNumber);
  };

  const handleAction = (menuItemSelected) => {
    switch (menuItemSelected) {
    case 'transfer_to_administrator': {
      getAdmins();
      setOpenTransferSetToAdminModal(true);
      break;
    }
    case 'transfer_to_teacher': {
      setOpenTransferSetToTeacherModal(true);
      break;
    }
    case 'export_to_xml_file': {
      exportToXmlAsync();
      break;
    }
    case 'delete': {
      setOpenDeleteSetModal(true);
      break;
    }
    case 'restore': {
      setShowRestore(!showRestore);
    }
    }
  };

  const clearSearch = () => {
    if (searchDebounceRef.current)
      clearTimeout(searchDebounceRef.current);
    setSearchText('');
    getSets(0, takeSize, '', showDeletedSets);
  };

  const handleDeleteQSetsAsync = async () => {
    if (isLoading)
      return;

    setIsLoading(true);
    try {
      let options = {
        ids: selectedSets.map((qSet) => qSet.id),
        isRestore: showRestore && selectedSets.some((qSet) => qSet.isDeleted && qSet.isRestorable)
      };

      let response = await deleteQuestionSets(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        if (showRestore && selectedSets.filter((qSet) => qSet.isDeleted).length > 0)
          CustomToast(`Deleted ${getSetType(activeTab)} set restored successfully.`, 'success');
        else
          CustomToast(`${getSetType(activeTab)} set deleted successfully.`, 'success');
        setSelectedSets([]);
        handlePageSelection(selectedSets.map((qSet) => qSet.id));
      }
    } catch (error) {
      console.log(error);
    }
    setOpenDeleteSetModal(false);
    setIsLoading(false);
  };

  const exportToXmlAsync = async () => {
    try {
      let options = {
        ids: selectedSets.map((qSet) => qSet.id)
      };
      await exportQuestionSetsToXml(options).unwrap();
    } catch (error) {
      if (error.data) {
        const XMLData = error.data;
        const blob = new Blob([XMLData], { type: 'text/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const fileName = 'CastleQuestionSets.xml';
        link.download = fileName;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
    setSelectedSets([]);
  };

  const handleTeacherSelect = (teacherId) => {
    if (teacherId == selectedTeacherId)
      setSelectedTeacherId('');
    else
      setSelectedTeacherId(teacherId);
  };

  useEffect(() => {
    const handler = setTimeout(async () => {
      const trimmedTeacherSearchText = teacherSearchText.trim();
      if (trimmedTeacherSearchText.length < 3) {
        setTeachers([]);
        setTeacherSearchInfo('');
        return;
      }

      try {
        const options = {
          searchText: trimmedTeacherSearchText
        };
        const response = await searchTeachers(options);
        if (response.error)
          CustomToast(response.error.data.error, 'error');
        else {
          setTeachers(response.data);
          setTeacherSearchInfo(response.data.length == 0 ? 'No Search Results Found!' : '');
        }
      } catch (error) {
        console.log(error);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [teacherSearchText, searchTeachers]);

  const handleTransferQSetsToTeacherAsync = async () => {
    if (isLoading)
      return;

    setIsLoading(true);
    try {
      let options = {
        ids: selectedSets.map((qSet) => qSet.id),
        teacherId: selectedTeacherId
      };

      let response = await transferQuestionSetsToTeacher(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        CustomToast(`${getSetType(activeTab)} set transfer completed successfully.`, 'success');
        setSelectedSets([]);
        setTeacherSearchText('');
        handlePageSelection(selectedSets.map((qSet) => qSet.id));
      }
    } catch (error) {
      console.log(error);
    }
    setOpenTransferSetToTeacherModal(false);
    setIsLoading(false);
  };

  const handleAdminSelect = (adminId) => {
    if (adminId == selectedAdminId)
      setSelectedAdminId('');
    else
      setSelectedAdminId(adminId);
  };

  const getAdmins = async () => {
    try {
      const response = await getAdminList();
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        setAdmins(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleTransferQSetsToAdminAsync = async () => {
    if (isLoading)
      return;

    setIsLoading(true);
    try {
      let options = {
        ids: selectedSets.map((qSet) => qSet.id),
        adminId: selectedAdminId
      };

      let response = await transferQuestionSetsToAdmin(options);
      if (response.error)
        CustomToast(response.error.data.error, 'error');
      else {
        CustomToast(`${getSetType(activeTab)} set transfer completed successfully.`, 'success');
        setSelectedSets([]);
        setAdminSearchText('');
        handlePageSelection(selectedSets.map((qSet) => qSet.id));
      }
    } catch (error) {
      console.log(error);
    }
    setOpenTransferSetToAdminModal(false);
    setIsLoading(false);
  };

  const handlePageSelection = (selectedSetIds) => {
    const isAllItemInSetListSelected = setsList.every(set => selectedSetIds.includes(set.qSetId));
    if (!isAllItemInSetListSelected)
      getSets((currentPage - 1) * takeSize, takeSize, searchText, showDeletedSets);
    else {
      let page = Math.max(currentPage - 1, 1);
      getSets((page - 1) * takeSize, takeSize, searchText, showDeletedSets);
      setCurrentPage(page);
    }
  };

  return (
    <>
      <Row className="py-2 align-items-center">
        <Col className="d-flex gap-3">
          <button onClick={() => setOpenCreateSet(true)} className="btn btn-castleRed fs-14 min-w-160">Add {getSetType(activeTab)} Set</button>
          {activeTab === 'questionsets' && [UserTypes.SuperAdministrator, UserTypes.Sales, UserTypes.SuperSales, UserTypes.Trainer].includes(user?.UserType) && (
            <button onClick={handleImportQuestionSet} className='btn outline-btn fs-14'>
              Import Question Set
            </button>
          )}
        </Col>
        {showDisplayDeletedSetCheckbox &&
          <Col className="d-flex align-items-center form-check col-auto">
            <input
              type="checkbox"
              id="displayDeletedSets"
              className="form-check-input mx-1"
              checked={showDeletedSets}
              onChange={() => { setSelectedSets([]); handleShowDeletedChanges(); setShowRestore(false); }}
            />
            <label className="form-check-label" htmlFor="displayDeletedSets">
              Display Deleted Sets
            </label>
          </Col>
        }
      </Row>
      <Row id='searchBox' className="mt-2 col-12 m-0">
        <Col className="custom-search position-relative p-0">
          <FontAwesomeIcon icon={faSearch} />
          <input
            type="text"
            placeholder="Filter Items"
            className="form-control px-5"
            id="searchQuestionSets"
            value={searchText}
            autoComplete="off"
            onChange={(e) => handleSearch(e.target.value)}
          />
          {(searchText && <FontAwesomeIcon
            icon={faXmark}
            className='fs-5 start-100 cursor-pointer ml-n25'
            onClick={clearSearch} />
          )}
        </Col>
      </Row>

      {setsList.length > 0 ? (
        <div id='qSetList' className="m-0 mt-4 gap-1 col-12 sets-container overflow-auto">
          {setsList.map((set, index) => (
            <>
              {(showDeletedSets || !set.deleted) &&
                <SetTile
                  activeTab={activeTab}
                  index={index}
                  set={set}
                  showDeletedSets={showDeletedSets}
                  setSetsList={setSetsList}
                  selectedSets={selectedSets}
                  setSelectedSets={setSelectedSets}
                  setShowRestore={setShowRestore}
                  selectedSetId={selectedSetId}
                  setSelectedSetId={setSelectedSetId}
                  selectedSetName={selectedSetName}
                  setSelectedSetName={setSelectedSetName}
                  setDateBasedUniqueKey={setDateBasedUniqueKey}
                />
              }
            </>
          ))}
        </div>
      ) : (
        <div className='noResultsContainer mt-3'>
          <div className='noResultsBox w-100'>
            <div className='noResultsIconBox mb-4'>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </div>
            <p className='fw-500'>No sets found.</p>
          </div>
        </div>
      )}

      {pageCount > 1 &&
        <PageNavigation
          currentPage={currentPage}
          pageCount={pageCount}
          handleSelectPage={handleSelectPage}
        />
      }

      {openImportXmlModal && (
        <ImportXmlModal
          isOpen={openImportXmlModal}
          importFile={importFile}
          isImportWaiting={isImportWaiting}
          importLoadingProgress={importLoadingProgress}
          handleImportFileChange={handleImportFileChange}
          handleUploadImportXml={handleUploadImportXml}
          onClose={closeModal}
        />
      )}

      <FloatingBar
        key={'floatingBar' + dateBasedUniqueKey}
        activeTab={activeTab}
        selectedItems={selectedSets}
        onAction={handleAction}
        showRestore={showRestore}
      />

      <Modal
        contentWidth="600px"
        isOpen={openDeleteSetModal}
        titleName={`${selectedSets.filter((qSet) => qSet.isDeleted).length > 0 ? 'Restore' : 'Delete'} Set <span>(${selectedSets[0]?.name}${selectedSets.length > 1 ? ` and ${selectedSets.length - 1} more...` : ''})</span>`}
        onClose={() => setOpenDeleteSetModal(false)}>
        {selectedSets.filter((qSet) => !qSet.isDeleted).length > 0 &&
          <>
            {selectedSets.filter((qSet) => qSet.isRestorable).length > 0 ?
              <>
                <p>If you have items in this set, then the set will be hidden.</p>
                <p>If this set is empty, it will be permanently deleted.</p>
              </> : <>
                <p>If you have items in this set, then the set will be permanently deleted and not recoverable.</p>
                <p>Note that this is for internal staff only!</p>
              </>
            }
          </>
        }
        <div className='align-items-center border d-flex mt-4 p-2 rounded-3'>
          <img src={deleteMenu} />
          <span className='pl-15'>Are you sure you want to {selectedSets.filter((qSet) => qSet.isDeleted).length > 0 ? 'restore' : 'delete'} this set?</span>
        </div>
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => { setOpenDeleteSetModal(false); }}>
            No
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={isLoading}
            onClick={() => handleDeleteQSetsAsync()}
          >
            Yes
          </button>
        </div>
      </Modal>

      <Modal
        contentWidth="60%"
        isOpen={openTransferSetToAdminModal}
        titleName={`Transfer Set to Administrator <span>(${selectedSets[0]?.name}${selectedSets.length > 1 ? ` and ${selectedSets.length - 1} more...` : ''})</span>`}
        onClose={() => { setOpenTransferSetToAdminModal(false); setAdminSearchText(''); setSelectedAdminId(''); }}>
        <div className='info-text d-flex mt-0'>
          <div>
            <span>
              <FontAwesomeIcon icon={faInfo} />
            </span>
          </div>
          <div className='text-justify'>
            <p>
              Select the administrator you want to transfer this set to. Note that once the set is transferred you lose ownership of the set and all its contents. Any assignment using the contents will have the questions quarantined.
            </p>
          </div>
        </div>
        <div className='pt-3'>
          <label>Administartor</label>
          <div className="custom-search position-relative mb-3">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              value={adminSearchText}
              onChange={(e) => setAdminSearchText(e.target.value)}
              autoComplete="off"
              className="form-control"
              placeholder='Find an Administrator'
            />
          </div>
          <div className='teacherSearch-wrapper transparent-scroll mb-3'>
            <div className="h-250 overflow-auto">
              {admins
                .filter(item =>
                  item.fName.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                  item.lName.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                  item.login.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                  item.email.toLowerCase().includes(adminSearchText.toLowerCase())
                ).map((member, index) => (
                  <div
                    key={index}
                    className="form-check-data d-flex justify-content-between align-items-center mb-3">
                    <label className="form-check-label ms-3" htmlFor="Optionsc">
                      {member.lName + ' ' + member.fName} ({member.login}) ({member.schoolName})
                    </label>
                    <div className={`fs-2 ms-auto questionSet-circular-check mx-2 cursor-pointer ${selectedAdminId === member.studentId ? 'check' : 'uncheck'}`} onClick={() => handleAdminSelect(member.studentId)}>
                      <FontAwesomeIcon icon={selectedAdminId === member.studentId ? faCircleCheck : faCircle} />
                    </div>
                  </div>
                ))}
              <p className='d-flex text-danger justify-content-center mt-3'>{
                adminSearchText.length > 0 && admins
                  .filter(item =>
                    item.fName.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                    item.lName.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                    item.login.toLowerCase().includes(adminSearchText.toLowerCase()) ||
                    item.email.toLowerCase().includes(adminSearchText.toLowerCase())
                  ).length == 0 && 'No Search Results Found!'}</p>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => { setOpenTransferSetToAdminModal(false); setAdminSearchText(''); setSelectedAdminId(''); }}>
            Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={!selectedAdminId || isLoading}
            onClick={() => handleTransferQSetsToAdminAsync()}
          >
            Save
          </button>
        </div>
      </Modal>

      <Modal
        contentWidth="60%"
        isOpen={openTransferSetToTeacherModal}
        titleName={`Transfer Set to Teacher <span>(${selectedSets[0]?.name}${selectedSets.length > 1 ? ` and ${selectedSets.length - 1} more...` : ''})</span>`}
        onClose={() => { setOpenTransferSetToTeacherModal(false); setTeacherSearchText(''); setTeachers([]); setSelectedTeacherId(''); }}>
        <div className='info-text d-flex mt-0'>
          <div>
            <span>
              <FontAwesomeIcon icon={faInfo} />
            </span>
          </div>
          <div className='text-justify'>
            <p>
              Select the teacher you want to transfer this set to. Note that once the set is transferred you lose ownership of the set and all its contents.
              After you enter at least three characters  the autocomplete function will show all possible matches.
            </p>
          </div>
        </div>
        <div className='pt-3'>
          <label>Teacher</label>
          <div className="custom-search position-relative mb-3">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              value={teacherSearchText}
              onChange={(e) => setTeacherSearchText(e.target.value)}
              autoComplete="off"
              className="form-control"
              placeholder='Find a Teacher (Type here to load teachers)'
            />
          </div>
          <div className='teacherSearch-wrapper transparent-scroll mb-3'>
            <div className="h-250 overflow-auto">
              {teachers.map((member, index) => (
                <div
                  key={index}
                  className="form-check-data d-flex justify-content-between align-items-center mb-3">
                  <label className="form-check-label ms-3" htmlFor="Optionsc">
                    {member.lName + ' ' + member.fName} ({member.login}) ({member.schoolName})
                  </label>
                  <div className={`fs-2 ms-auto questionSet-circular-check mx-2 cursor-pointer ${selectedTeacherId === member.studentId ? 'check' : 'uncheck'}`} onClick={() => handleTeacherSelect(member.studentId)}>
                    <FontAwesomeIcon icon={selectedTeacherId === member.studentId ? faCircleCheck : faCircle} />
                  </div>
                </div>
              ))}
              <p className='d-flex text-danger justify-content-center mt-3'>{teacherSearchInfo}</p>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-3 gap-3 align-items-center">
          <button className="btn btn-castleGray btn-width" onClick={() => { setOpenTransferSetToTeacherModal(false); setTeacherSearchText(''); setTeachers([]); setSelectedTeacherId(''); }}>
            Cancel
          </button>
          <button className="btn btn-castleRed btn-width"
            disabled={!selectedTeacherId || isLoading}
            onClick={() => handleTransferQSetsToTeacherAsync()}
          >
            Save
          </button>
        </div>
      </Modal>
    </>
  );
};

SetListing.propTypes = {
  setSetsList: PropTypes.func,
  setsList: PropTypes.array,
  setOpenCreateSet: PropTypes.func,
  setSearchText: PropTypes.func,
  searchText: PropTypes.string,
  pageCount: PropTypes.number,
  setCurrentPage: PropTypes.func,
  currentPage: PropTypes.number,
  getSets: PropTypes.func,
  setSkip: PropTypes.func,
  takeSize: PropTypes.number,
  showDeletedSets: PropTypes.bool,
  handleShowDeletedChanges: PropTypes.func,
  isSearching: PropTypes.bool,
  setIsSearching: PropTypes.func,
  showDisplayDeletedSetCheckbox: PropTypes.bool
};

export default SetListing;
